<template>
  <div class="h-full flex bg-slate-50">
    <UserModal 
      :is-open="showUserModal" 
      @close="showUserModal = false"
      @register="handleUserRegistered"
    />
    <!-- Backdrop for mobile -->
    <div 
      v-if="isMobileSidebarOpen" 
      @click="isMobileSidebarOpen = false"
      class="fixed inset-0 bg-black/50 z-[95] md:hidden transition-opacity duration-300"
    ></div>

    <!-- Full-height Desktop sidebar fixed to cross up -->
    <aside
      v-if="hasAllPrivilege"
      class="tablet-sidebar flex flex-col bg-[#0d4169] text-white border-r border-white/10 shadow-xl fixed top-0 bottom-0 left-0 z-[100]"
      :class="{ 'mobile-sidebar-open': isMobileSidebarOpen, 'tablet-sidebar-hidden': !tabletSidebarVisible }"
    >
      <!-- Professional Logo Section: Same background as list, separated by grey-near-white line -->
      <div class="h-24 flex flex-col items-center justify-center px-2 border-b border-white/20">
        <div class="h-12 w-12 rounded-full shadow-xl mb-1 group hover:scale-105 transition-all cursor-pointer overflow-hidden border border-white/10">
          <div
            class="h-full w-full rounded-full bg-[#0d4169] flex items-center justify-center overflow-hidden"
          >
            <img
              v-if="sidebarLogo"
              :src="sidebarLogo"
              :alt="sidebarLogoAlt"
              class="h-full w-full object-cover"
            />
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-white"
            >
              <!-- user circle icon -->
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
        </div>
        <span class="text-[10px] font-black text-white">
          {{ companyName || 'Mina' }}   {{ branchName? " - " + branchName : '' }}
        </span> 
         <!-- <span v-if="branchName" class="text-[10px] font-black text-white">
         -  {{ branchName }}
        </span> -->
      </div>

      <nav v-if="canSeeSidebarLists" class="flex-1 py-4 space-y-0.5 px-0 sidebar overflow-y-auto">
        <template v-for="item in sidebarItems" :key="item.key">
          <!-- Normal Nav Item (Everything except Inventory to put Register before it) -->
          <button
            v-if="item.key !== 'inventory' && item.key !== 'register_trigger' && item.key !== 'production' && item.key !== 'central_kitchen' && item.key !== 'debones' && item.key !== 'pos' && item.key !== 'finance' && item.key !== 'hr' && item.key !== 'crm'"
            type="button"
            class="w-full flex items-center gap-3 pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 active:scale-[0.97] group relative overflow-hidden"
            :class="
              (active === item.key || (active === 'pos' && item.key === 'waiter_orders'))
                ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg shadow-sm'
                : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent'
            "
            @click="handleNavClick(item.key)"
          >
            <div class="flex-shrink-0 transition-all duration-300 group-hover:scale-110 text-white">
              <svg v-if="item.key === 'dashboard'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
              <svg v-else-if="item.key === 'users'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              <svg v-else-if="item.key === 'finance'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
              <svg v-else-if="item.key === 'production'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M17 18h1"></path><path d="M12 18h1"></path><path d="M7 18h1"></path></svg>
              <svg v-else-if="item.key === 'pos' || item.key === 'waiter_orders'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
              <svg v-else-if="item.key === 'hr'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg>
              <svg v-else-if="item.key === 'settings'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1-2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>
              <svg v-else-if="item.key === 'logout'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
            </div>
            <span class="whitespace-nowrap">{{ item.label }}</span>
          </button>

          <!-- Register Section (Injected after Users) -->
          <template v-if="item.key === registerInsertAfterKey && hasRegisterUsersPrivilege">
            <div class="relative overflow-visible">
              <button
                type="button"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 active:scale-[0.97] group relative overflow-hidden"
                :class="showRegisterDropdown ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg' : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent'"
                @click="toggleRegister"
              >
                <div class="flex items-center gap-3">
                  <div class="flex-shrink-0 transition-all duration-300 group-hover:scale-110 text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="12" y1="18" x2="12" y2="12"></line><line x1="9" y1="15" x2="15" y2="15"></line></svg>
                  </div>
                  <span class="whitespace-nowrap">Register</span>
                </div>
                <svg 
                  class="w-3 h-3 transition-transform duration-300" 
                  :class="showRegisterDropdown ? 'rotate-180' : ''" 
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              <!-- Dropdown Menu -->
              <Transition name="dropdown">
                <div v-if="showRegisterDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <button
                    v-for="sub in filteredRegisterItems"
                    :key="sub.key"
                    class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                    :class="active === sub.key 
                      ? 'text-white bg-white/15 border-white' 
                      : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                    @click="handleNavClick(sub.key)"
                  >
                    {{ sub.label }}
                  </button>
                </div>
              </Transition>
            </div>
          </template>

          <!-- Inventory (Transformed into Dropdown) -->
          <template v-if="item.key === 'inventory'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasStockModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasStockModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showInventoryDropdown
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleInventory"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasStockModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.28"></line><polyline points="21 16 12 21 3 16"></polyline><polyline points="3 7 12 12 21 7"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasStockModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showInventoryDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              <!-- Dropdown Menu -->
              <Transition name="dropdown">
                <div v-if="hasStockModuleAccess && showInventoryDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <div v-for="sub in filteredInventoryItems" :key="sub.key" class="relative">
                    <template v-if="sub.key !== 'report_list'">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                        :class="active === sub.key 
                          ? 'text-white bg-white/15 border-white' 
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="handleNavClick(sub.key)"
                      >
                        {{ sub.label }}
                      </button>
                    </template>

                  <template v-else>
                    <div class="relative overflow-visible">
                      <button
                        type="button"
                        class="w-full flex items-center justify-between pl-6 pr-4 py-2 text-[13px] font-semibold transition-all duration-200 text-white"
                        :class="showReportDropdown ? 'bg-white/10 text-white' : ''"
                        @click.stop="toggleReport"
                      >
                        <div class="flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-white mr-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <rect x="3" y="14" width="3" height="7" rx="0.5" fill="currentColor" />
                            <rect x="9" y="10" width="3" height="11" rx="0.5" fill="currentColor" />
                            <rect x="15" y="6" width="3" height="15" rx="0.5" fill="currentColor" />
                          </svg>
                          <span :class="showReportDropdown ? 'whitespace-nowrap text-white font-bold' : 'whitespace-nowrap'">{{ sub.label }}</span>
                        </div>
                        <svg class="w-3 h-3 transition-transform duration-200" :class="showReportDropdown ? 'rotate-180' : ''" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>

                      <Transition name="dropdown">
                        <div v-if="showReportDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                            <button
                              v-for="r in filteredReportItems"
                              :key="r.key"
                              class="w-full pl-6 pr-2 py-2 text-[11px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                              :class="active === r.key 
                                ? 'text-white bg-white/15 border-white' 
                                : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                              @click="handleNavClick(r.key)"
                            >
                            {{ r.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                  </template>
                </div>
              </div>
            </Transition>
            </div>
          </template>
          <!-- Production Section (similar to Inventory) -->
          <template v-if="item.key === 'production'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasProductionModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasProductionModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showProductionDropdown
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleProduction"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasProductionModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <!-- reuse production icon -->
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path><path d="M17 18h1"></path><path d="M12 18h1"></path><path d="M7 18h1"></path></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasProductionModuleAccess"
                  class="w-3 h-3 transition-transform duration-300" 
                  :class="showProductionDropdown ? 'rotate-180' : ''" 
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <Transition name="dropdown">
                <div v-if="hasProductionModuleAccess && showProductionDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <template v-for="p in filteredProductionItems" :key="p.key">
                    <button
                      v-if="p.key !== 'production_register' && p.key !== 'production_posting' && p.key !== 'production_report'"
                      class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                      :class="active === p.key 
                        ? 'text-white bg-white/15 border-white' 
                        : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                      @click="handleNavClick(p.key)"
                    >
                      {{ p.label }}
                    </button>
                    <div v-else-if="p.key === 'production_register'" class="relative">
                      <button
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === p.key || showProductionRegisterDropdown
                          ? 'text-white bg-white/15 border-white' 
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleProductionRegister"
                      >
                        {{ p.label }}
                        <svg 
                          class="w-2.5 h-2.5 transition-transform duration-300" 
                          :class="showProductionRegisterDropdown ? 'rotate-180' : ''" 
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showProductionRegisterDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="pr in filteredProductionRegisterItems" :key="pr.key"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === pr.key 
                              ? 'text-white bg-white/20 border-white' 
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(pr.key)"
                          >
                            {{ pr.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="p.key === 'production_posting'" class="relative">
                      <button
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === p.key || showProductionPostingDropdown
                          ? 'text-white bg-white/15 border-white' 
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleProductionPosting"
                      >
                        {{ p.label }}
                        <svg 
                          class="w-2.5 h-2.5 transition-transform duration-300" 
                          :class="showProductionPostingDropdown ? 'rotate-180' : ''" 
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showProductionPostingDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="pp in filteredProductionPostingItems" :key="pp.key"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === pp.key 
                              ? 'text-white bg-white/20 border-white' 
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(pp.key)"
                          >
                            {{ pp.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="p.key === 'production_report'" class="relative">
                      <button
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === p.key || showProductionReportDropdown
                          ? 'text-white bg-white/15 border-white' 
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleProductionReport"
                      >
                        {{ p.label }}
                        <svg 
                          class="w-2.5 h-2.5 transition-transform duration-300" 
                          :class="showProductionReportDropdown ? 'rotate-180' : ''" 
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showProductionReportDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="pr in filteredProductionReportItems" :key="pr.key"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === pr.key 
                              ? 'text-white bg-white/20 border-white' 
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(pr.key)"
                          >
                            {{ pr.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else class="relative">
                      <button
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                        :class="active === p.key 
                          ? 'text-white bg-white/15 border-white' 
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="handleNavClick(p.key)"
                      >
                        {{ p.label }}
                      </button>
                    </div>
                  </template>
                </div>
              </Transition>
            </div>
          </template>

          <!-- Debones Section (top-level, same as Cnt. Kitchen) -->
          <template v-if="item.key === 'debones'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasDebonesModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasDebonesModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showDebonesDropdown
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleDebones"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasDebonesModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><line x1="10" y1="9" x2="8" y2="9"></line></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasDebonesModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showDebonesDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <Transition name="dropdown">
                <div v-if="hasDebonesModuleAccess && showDebonesDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <button
                    v-for="db in filteredDebonesItems"
                    :key="db.key"
                    class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                    :class="active === db.key
                      ? 'text-white bg-white/15 border-white'
                      : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                    @click="handleNavClick(db.key)"
                  >
                    {{ db.label }}
                  </button>
                </div>
              </Transition>
            </div>
          </template>

          <!-- Central Kitchen Section (top-level, same as Production) -->
          <template v-if="item.key === 'central_kitchen'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasCentralKitchenModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasCentralKitchenModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showCentralKitchenDropdown
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleCentralKitchen"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasCentralKitchenModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"></path><path d="M7 2v20"></path><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"></path></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasCentralKitchenModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showCentralKitchenDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <Transition name="dropdown">
                <div v-if="hasCentralKitchenModuleAccess && showCentralKitchenDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <template v-for="ck in filteredCentralKitchenItems" :key="ck.key">
                    <button
                      v-if="ck.key !== 'central_report'"
                      class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                      :class="active === ck.key
                        ? 'text-white bg-white/15 border-white'
                        : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                      @click="handleNavClick(ck.key)"
                    >
                      {{ ck.label }}
                    </button>
                    <div v-else class="relative">
                      <button
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === ck.key || showCentralReportDropdown
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleCentralReport"
                      >
                        {{ ck.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showCentralReportDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showCentralReportDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="cr in filteredCentralReportItems"
                            :key="cr.key"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === cr.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(cr.key)"
                          >
                            {{ cr.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                  </template>
                </div>
              </Transition>
            </div>
          </template>
          
          <!-- POS Section -->
          <template v-if="item.key === 'pos'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasPosModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasPosModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showPosDropdown
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="togglePos"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasPosModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasPosModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showPosDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              <!-- Dropdown Menu -->
              <Transition name="dropdown">
                <div v-if="hasPosModuleAccess && showPosDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <button
                    v-for="sub in filteredPosItems"
                    :key="sub.key"
                    class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                    :class="active === sub.key 
                      ? 'text-white bg-white/15 border-white' 
                      : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                    @click="handleNavClick(sub.key)"
                  >
                    {{ sub.label }}
                  </button>
                </div>
              </Transition>
            </div>
          </template>

          <!-- Finance Section -->
          <template v-if="item.key === 'finance'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasFinanceModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasFinanceModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showFinanceDropdown || active === 'finance' || active === 'finance_report'
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleFinance"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasFinanceModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasFinanceModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showFinanceDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <Transition name="dropdown">
                <div v-if="hasFinanceModuleAccess && showFinanceDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <button
                    v-for="sub in filteredFinanceItems"
                    :key="sub.key"
                    type="button"
                    class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                    :class="active === sub.key
                      ? 'text-white bg-white/15 border-white'
                      : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                    @click="handleNavClick(sub.key)"
                  >
                    {{ sub.label }}
                  </button>
                </div>
              </Transition>
            </div>
          </template>

          <!-- HR Section (module-gated; dropdown only for now) -->
          <template v-if="item.key === 'hr'">
            <div class="relative overflow-visible">
              <button
                type="button"
                :disabled="!hasHrModuleAccess"
                class="w-full flex items-center justify-between pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
                :class="!hasHrModuleAccess
                  ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                  : showHrDropdown || hrItems.some(i => i.key === active) || hrSettingsItems.some(i => i.key === active) || hrAdvancedSettingsItems.some(i => i.key === active) || hrEmployeeItems.some(i => i.key === active) || hrAttendanceItems.some(i => i.key === active) || hrReportItems.some(i => i.key === active) || active === 'hr_payroll_approval'
                    ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg active:scale-[0.97]'
                    : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
                @click="toggleHr"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="flex-shrink-0 transition-all duration-300 text-white"
                    :class="hasHrModuleAccess ? 'group-hover:scale-110' : ''"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><polyline points="16 11 18 13 22 9"></polyline></svg>
                  </div>
                  <span class="whitespace-nowrap">{{ item.label }}</span>
                </div>
                <svg
                  v-if="hasHrModuleAccess"
                  class="w-3 h-3 transition-transform duration-300"
                  :class="showHrDropdown ? 'rotate-180' : ''"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              <Transition name="dropdown">
                <div v-if="hasHrModuleAccess && showHrDropdown" class="bg-[#245ea6] backdrop-blur-md overflow-hidden">
                  <template v-for="sub in filteredHrItems" :key="sub.key">
                    <button
                      v-if="sub.key !== 'hr_settings' && sub.key !== 'hr_advanced_settings' && sub.key !== 'hr_employee' && sub.key !== 'hr_attendance' && sub.key !== 'hr_report'"
                      type="button"
                      class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap text-left"
                      :class="active === sub.key
                        ? 'text-white bg-white/15 border-white'
                        : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                      @click="handleNavClick(sub.key)"
                    >
                      {{ sub.label }}
                    </button>
                    <div v-else-if="sub.key === 'hr_settings'" class="relative">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === sub.key || showHrSettingsDropdown || hrSettingsItems.some(i => i.key === active)
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleHrSettings"
                      >
                        {{ sub.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showHrSettingsDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showHrSettingsDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="hs in filteredHrSettingsItems"
                            :key="hs.key"
                            type="button"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === hs.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(hs.key)"
                          >
                            {{ hs.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="sub.key === 'hr_advanced_settings'" class="relative">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === sub.key || showHrAdvancedSettingsDropdown || hrAdvancedSettingsItems.some(i => i.key === active)
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleHrAdvancedSettings"
                      >
                        {{ sub.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showHrAdvancedSettingsDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showHrAdvancedSettingsDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="ha in filteredHrAdvancedSettingsItems"
                            :key="ha.key"
                            type="button"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === ha.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(ha.key)"
                          >
                            {{ ha.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="sub.key === 'hr_employee'" class="relative">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === sub.key || showHrEmployeeDropdown || hrEmployeeItems.some(i => i.key === active)
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleHrEmployee"
                      >
                        {{ sub.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showHrEmployeeDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showHrEmployeeDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="he in filteredHrEmployeeItems"
                            :key="he.key"
                            type="button"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === he.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(he.key)"
                          >
                            {{ he.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="sub.key === 'hr_attendance'" class="relative">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === sub.key || showHrAttendanceDropdown || hrAttendanceItems.some(i => i.key === active)
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleHrAttendance"
                      >
                        {{ sub.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showHrAttendanceDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showHrAttendanceDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="ha in filteredHrAttendanceItems"
                            :key="ha.key"
                            type="button"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === ha.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(ha.key)"
                          >
                            {{ ha.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                    <div v-else-if="sub.key === 'hr_report'" class="relative">
                      <button
                        type="button"
                        class="w-full pl-6 pr-2 py-2 text-[12px] font-semibold transition-all duration-300 border-l-2 whitespace-nowrap flex items-center justify-between text-left"
                        :class="active === sub.key || showHrReportDropdown || hrReportItems.some(i => i.key === active)
                          ? 'text-white bg-white/15 border-white'
                          : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                        @click="toggleHrReport"
                      >
                        {{ sub.label }}
                        <svg
                          class="w-2.5 h-2.5 transition-transform duration-300"
                          :class="showHrReportDropdown ? 'rotate-180' : ''"
                          xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                      <Transition name="dropdown">
                        <div v-if="showHrReportDropdown" class="bg-[#538dd6] backdrop-blur-md overflow-hidden">
                          <button
                            v-for="hr in filteredHrReportItems"
                            :key="hr.key"
                            type="button"
                            class="w-full pl-8 pr-2 py-1.5 text-[11px] font-medium transition-all duration-300 border-l-2 whitespace-nowrap flex items-center text-left"
                            :class="active === hr.key
                              ? 'text-white bg-white/20 border-white'
                              : 'text-white hover:bg-white/10 border-transparent hover:border-white/40'"
                            @click="handleNavClick(hr.key)"
                          >
                            {{ hr.label }}
                          </button>
                        </div>
                      </Transition>
                    </div>
                  </template>
                </div>
              </Transition>
            </div>
          </template>

          <!-- CRM Section (module-gated; no page implementation yet) -->
          <template v-if="item.key === 'crm'">
            <button
              type="button"
              :disabled="!hasCrmModuleAccess"
              class="w-full flex items-center gap-3 pl-3 pr-4 py-3 text-[13px] font-bold transition-all duration-300 group relative overflow-hidden"
              :class="!hasCrmModuleAccess
                ? 'text-white/45 cursor-not-allowed opacity-60 border-t border-transparent'
                : active === item.key
                  ? 'bg-white/10 text-white border-t-4 border-white rounded-t-lg shadow-sm active:scale-[0.97]'
                  : 'text-white hover:bg-white/5 hover:text-white border-t border-transparent active:scale-[0.97]'"
              @click="handleNavClick(item.key)"
            >
              <div
                class="flex-shrink-0 transition-all duration-300 text-white"
                :class="hasCrmModuleAccess ? 'group-hover:scale-110' : ''"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
              </div>
              <span class="whitespace-nowrap">{{ item.label }}</span>
            </button>
          </template>
        </template>
      </nav>
    </aside>

    <!-- Main area: Adjusted margin to account for fixed sidebar -->
    <div class="tablet-main flex-1 flex flex-col h-full overflow-hidden" :class="{ 'tablet-main-expanded': hasAllPrivilege && !tabletSidebarVisible }">
      <!-- Secondary White Header (hidden for non-item Inventory child pages) -->
      <header
        v-if="!hideHeader"
        class="shrink-0 bg-white border-b border-slate-200 shadow-sm"
        :class="active === 'settings'
          ? 'flex flex-col gap-3 px-4 py-3 md:h-14 md:flex-row md:items-center md:justify-between md:px-6 md:py-0'
          : 'h-14 flex items-center justify-between px-4 md:px-6'"
      >
        <div class="flex items-center justify-between w-full gap-3 min-w-0 md:w-auto md:flex-initial">
          <div class="flex items-center gap-3 min-w-0">
            <!-- <span class="text-[10px] text-slate-400 font-bold">
              System Console
            </span> -->
            <span class="text-sm text-slate-800">
              <span class="font-black">{{ breadcrumb.parent }}</span>
              <template v-if="breadcrumb.child">
                <span class="mx-1 text-slate-400 font-medium">/</span>
                <span class="font-medium text-slate-600 lowercase">{{ breadcrumb.child }}</span>
              </template>
            </span>
          </div>

          <button
            v-if="active === 'settings'"
            type="button"
            @click="handleChangePassword"
            class="md:hidden shrink-0 px-3 py-1.5 bg-white hover:bg-slate-50 text-[#0d4169] border border-[#0d4169]/20 text-xs font-bold rounded-lg transition-all shadow-sm flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            Change Password
          </button>
        </div>

        <div v-if="active === 'users' || active === 'item' || active === 'department' || active === 'central_departments' || active === 'category' || active === 'roles' || active === 'supplier' || active === 'menu_list'" class="flex items-center shrink-0">
          <button 
            v-if="active === 'users'"
            @click="handleAddUser"
            :disabled="!hasUsersAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasUsersAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New User
          </button>
          <button 
            v-else-if="active === 'item'"
            @click="handleAddItem"
            :disabled="!hasItemRegisterAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasItemRegisterAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Item
          </button>
          <button 
            v-else-if="active === 'department'"
            @click="handleAddDepartment"
            :disabled="!hasDepartmentAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasDepartmentAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Department
          </button>
          <button
            v-else-if="active === 'central_departmentsNew'"
            @click="handleAddCentralDepartment"
            :disabled="!hasCentralDepartmentAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasCentralDepartmentAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Central Department
          </button>
          <button 
            v-else-if="active === 'category'"
            @click="handleAddCategory"
            :disabled="!hasCategoryAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasCategoryAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Category
          </button>
          <button 
            v-else-if="active === 'roles'"
            @click="handleAddRole"
            :disabled="!hasRolesAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasRolesAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Role
          </button>
          <button 
            v-else-if="active === 'supplier'"
            @click="handleAddSupplier"
            :disabled="!hasSupplierAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasSupplierAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Supplier
          </button>
          <button 
            v-else-if="active === 'menu_list'"
            @click="handleAddMenu"
            :disabled="!hasMenuListAddPrivilege"
            class="px-4 py-2 bg-[#0d4169] text-white text-xs font-bold rounded-lg transition-all shadow-md shadow-[#0d4169]/10 flex items-center gap-2"
            :class="hasMenuListAddPrivilege ? 'hover:bg-[#0a3354]' : 'opacity-50 cursor-not-allowed'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
            Add New Menu
          </button>
        </div>

        <div v-else-if="active === 'settings'" class="w-full md:w-auto flex flex-col gap-3 md:flex-row md:items-center md:justify-end md:gap-3 min-w-0">
          <div
            v-if="hasChangeServerDatePrivilege"
            class="w-full flex flex-wrap items-center gap-2 min-w-0"
          >
            <label class="w-full sm:w-auto text-xs font-semibold text-slate-600">Change Server Date</label>
            <input
              v-model="serverDateTimeForm.date"
              type="date"
              :disabled="serverDateModal.saving"
              class="flex-1 min-w-[130px] max-w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60"
            >
            <input
              v-model="serverDateTimeForm.time"
              type="time"
              step="1"
              :disabled="serverDateModal.saving"
              class="flex-1 min-w-[110px] max-w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-60"
            >
            <button
              type="button"
              @click="onServerDateTimeSelected"
              :disabled="serverDateModal.saving"
              class="shrink-0 px-3 py-1.5 bg-[#0d4169] text-white text-xs font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
            >
              Set
            </button>
          </div>
          <button
            type="button"
            @click="handleChangePassword"
            class="hidden md:flex shrink-0 px-4 py-2 bg-white hover:bg-slate-50 text-[#0d4169] border border-[#0d4169]/20 text-xs font-bold rounded-lg transition-all shadow-sm items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            Change Password
          </button>
        </div>
      </header>

      <!-- Content Area -->
      <main class="flex-1 p-5 overflow-y-auto flex flex-col bg-slate-50">
        <slot />
      </main>
    </div>
  </div>

  <!-- Change Server Date confirmation -->
  <div
    v-if="serverDateModal.open"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeServerDateModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white rounded-t-xl">
        <h3 class="text-base font-bold m-0">Change Server Date</h3>
      </div>
      <div class="p-5 space-y-3">
        <p class="text-sm text-slate-700 m-0">
          Are you sure you want to change the server date and time to
          <span class="font-bold text-[#0d4169]">{{ serverDateModal.dateTime }}</span>?
        </p>
        <p v-if="serverDateModal.error" class="text-xs text-red-600 font-semibold m-0">{{ serverDateModal.error }}</p>
      </div>
      <div class="px-5 py-4 flex items-center justify-end gap-2 border-t border-slate-100">
        <button
          type="button"
          :disabled="serverDateModal.saving"
          @click="closeServerDateModal"
          class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="serverDateModal.saving"
          @click="confirmChangeServerDate"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ serverDateModal.saving ? 'Updating...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Toast Notification -->
  <Transition name="toast">
    <div v-if="toast.show" class="fixed top-4 right-4 z-[1000] max-w-sm">
      <div
        :class="[
          'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[300px]',
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        ]"
      >
        <span v-if="toast.type === 'success'">✅</span>
        <span v-else>❌</span>
        <span class="font-medium">{{ toast.message }}</span>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import UserModal from './users/UserModal.vue'
import store from '../store'
import { apiFetch } from '../utils/api'

const router = useRouter()
const route = useRoute()
const active = ref(route.name || 'dashboard')
const showUserModal = ref(false)
const showRegisterDropdown = ref(false)
const showInventoryDropdown = ref(false)
const showReportDropdown = ref(false)
const showProductionDropdown = ref(false)
const showProductionRegisterDropdown = ref(false)
const showProductionPostingDropdown = ref(false)
const showCentralKitchenDropdown = ref(false)
const showDebonesDropdown = ref(false)
const showProductionReportDropdown = ref(false)
const showCentralReportDropdown = ref(false)
const showPosDropdown = ref(false)
const showFinanceDropdown = ref(false)
const showHrDropdown = ref(false)
const showHrSettingsDropdown = ref(false)
const showHrAdvancedSettingsDropdown = ref(false)
const showHrEmployeeDropdown = ref(false)
const showHrAttendanceDropdown = ref(false)
const showHrReportDropdown = ref(false)
const isMobileSidebarOpen = ref(false)
const tabletSidebarVisible = ref(true)

const currentUser = computed(() => store.getters.currentUser)
const loginSource = computed(() => store.getters.loginSource)

const companyName = computed(() => currentUser.value?.company?.name || null)
const branchName = computed(() => currentUser.value?.branch?.name || null)
const companyLogo = computed(() => currentUser.value?.companyLogo || null)
const branchLogo = computed(() => (
  currentUser.value?.branchLogo ||
  currentUser.value?.branch?.logo_url ||
  null
))
const sidebarLogo = computed(() => branchLogo.value || companyLogo.value || null)
const sidebarLogoAlt = computed(() => {
  if (branchLogo.value) return branchName.value || 'Branch Logo'
  return companyName.value || 'Company Logo'
})
//  alert(currentUser.value.companyLogo)
const registerItems = [
  { key: 'branch', label: 'Branch' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'department', label: 'Department' },
  // { key: 'stores', label: 'Stores' },
  { key: 'category', label: 'Stock Category' },
  // { key: 'item', label: 'Item' },
  { key: 'roles', label: 'Roles' },
  { key: 'menu_list', label: 'Menu List' },
]

const registerPrivilegeMap = {
  branch: 'Branch',
  supplier: 'Supplier',
  department: 'Department',
  category: 'Stock Category',
  roles: 'Roles',
  menu_list: 'Menu List'
}

const inventoryItems = [
  { key: 'item', label: 'Item Register' },
  { key: 'grv', label: 'GRV' },

  { key: 'siv', label: 'SIV (Internal Issue)' },
  { key: 'non_stockable', label: 'Non- Stockable' },
  { key: 'store_request', label: 'Store Request' },
  { key: 'purchase_request', label: 'Purchase Request' },

  { key: 'return', label: 'Return' },
  { key: 'adjust_stock', label: 'Stock Adjustment' },
  { key: 'approval', label: 'Approval' },
  { key: 'report_list', label: 'Sto. Report' },
]

const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string' 
    ? JSON.parse(currentUser.value.privilege) 
    : currentUser.value.privilege
})

const hasAdminPrivilege = computed(() => {
  // Support both { All: true } and { all: true }
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

const hasChangeServerDatePrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  const priv = getPrivilegeBlock('Change Server Date')
  return !!(priv?.Edit)
})

const serverDateTimeForm = ref({
  date: '',
  time: '00:00:00',
})
const serverDateModal = ref({
  open: false,
  dateTime: '',
  saving: false,
  error: '',
})

const toast = ref({ show: false, message: '', type: 'success' })

function showToast(message, type = 'success') {
  toast.value = { show: true, message, type }
  setTimeout(() => {
    toast.value.show = false
  }, 3000)
}

function parseModuleList(value) {
  if (!value) return []
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

function moduleListIncludes(modules, moduleName) {
  return parseModuleList(modules).includes(moduleName)
}

function hasAnyActionPrivilege(privObj) {
  if (!privObj) return false
  return Object.values(privObj).some((value) => value === true)
}

function hasAddEditViewPrivilege(privObj) {
  if (!privObj) return false
  return !!(privObj.Add || privObj.Edit || privObj.View)
}

function hasAddPrivilege(privObj) {
  if (!privObj) return false
  return !!privObj.Add
}

function hasViewPrivilege(privObj) {
  if (!privObj) return false
  return !!privObj.View
}

function getPrivilegeBlock(name) {
  // Support both exact-key and lowercase-key privilege blocks
  return userPrivileges.value?.[name] || userPrivileges.value?.[String(name).toLowerCase()] || null
}

const hasPosPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  const posPrivs = userPrivileges.value?.POS || userPrivileges.value?.pos
  if (!posPrivs) return false
  return posPrivs.Add || posPrivs.Edit || posPrivs.View || posPrivs.Void
})

const hasUsersPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAnyActionPrivilege(getPrivilegeBlock('Users'))
})

function resolveUserModules(user) {
  if (!user) return null
  const rawBranchId = user.branchId ?? user.branch_id ?? user.branch?.id ?? null
  const hasBranchId = rawBranchId != null && rawBranchId !== '' && Number(rawBranchId) > 0
  return hasBranchId ? user.branch?.modules : user.company?.modules
}

const hasStockModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'Stock')
})

const hasFinanceModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'Finance')
})

const hasProductionModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'Production')
})

const hasStockPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAddEditViewPrivilege(getPrivilegeBlock('Stock'))
})

const hasFinancePrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAddEditViewPrivilege(getPrivilegeBlock('Finance'))
})

const hasProductionPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAddEditViewPrivilege(getPrivilegeBlock('Production'))
})

const hasPosModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'POS/POO')
})

const hasDebonesModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'Debones')
})

const hasCentralKitchenModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'Central Kitchen')
})

const hasHrModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'HR')
})

const hasCrmModuleAccess = computed(() => {
  return moduleListIncludes(resolveUserModules(currentUser.value), 'CRM')
})

const hasCentralKitchenPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  // Only show Cnt. Kitchen top-level when user has Cnt. Kitchen (Add/Edit/View) explicitly
  return hasAddEditViewPrivilege(getPrivilegeBlock('Cnt. Kitchen'))
})

const hasDebonesPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  // Only show Debones top-level when user has Debones (Add/Edit/View) explicitly
  return hasAddEditViewPrivilege(getPrivilegeBlock('Debones'))
})

const hasHrPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  if (hasAnyActionPrivilege(getPrivilegeBlock('HR'))) return true
  // Show HR when at least one HR privilege checkbox is available
  const hrPrivilegeNames = [
    'HR Register', 'HR Company Profile', 'HR Leaving Type', 'HR Performance Criteria',
    'HR Advanced Register', 'HR Work/Shift Time', 'HR Overtime Type', 'HR Earn And Deduct', 'HR Pension And TAX',
    'HR Employee', 'HR Find Employee', 'HR Leaving Request', 'HR Employee Evaluation', 'HR Employee Letter',
    'HR Attendance', 'HR Attendance Configuration', 'HR Earn And Deduct', 'HR Attendance Configuration', 'HR Device Attendance',
    'HR Payroll', 'HR Payroll Approval', 'HR Report', 'HR Payroll Report', 'HR Leaving Request Report', 'HR Evaluation Report', 'HR Letter Report',
  ]
  return hrPrivilegeNames.some((name) => hasAnyActionPrivilege(getPrivilegeBlock(name)))
})

const hasCrmPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAnyActionPrivilege(getPrivilegeBlock('CRM'))
})

const hasRegisterUsersPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return hasAnyActionPrivilege(getPrivilegeBlock('Register Users'))
})

const filteredRegisterItems = computed(() => {
  if (hasAdminPrivilege.value) return registerItems

  return registerItems.filter(item => {
    const privilegeName = registerPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hasUsersAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Users')?.Add
})

const hasSupplierAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Supplier')?.Add
})

const hasDepartmentAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Department')?.Add
})

const hasCentralDepartmentAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Central Departments')?.Add
})

const hasCategoryAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Stock Category')?.Add
})

const hasRolesAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Roles')?.Add
})

const hasMenuListAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Menu List')?.Add
})

const hasItemRegisterAddPrivilege = computed(() => {
  if (hasAdminPrivilege.value) return true
  return !!getPrivilegeBlock('Item Register')?.Add
})

const hasAllPrivilege = computed(() => {
  return true // Condition removed for now
})



const canSeeSidebarLists = computed(() => {
  return true // Condition removed for now
})

const reportItems = [
  { key: 'report_balance', label: 'Balance Report' },
  { key: 'report_saved', label: 'Saved Report' },
  { key: 'report_grv', label: 'GRV Report' },
  { key: 'report_siv', label: 'SIV Report' },
  { key: 'report_approval_request', label: 'Store Request Report' },
  { key: 'report_non_stockable', label: 'Non- Stockable Report' },
  { key: 'report_return', label: 'Return Report' },
  { key: 'report_adjust_stock', label: 'Adjustment Report' },
  { key: 'report_purchase', label: 'Purchase Report' },
]

const filteredReportItems = computed(() => {
  if (hasAdminPrivilege.value) return reportItems

  const reportPrivilegeMap = {
    report_balance: 'Balance Report',
    report_saved: 'Saved Report',
    report_grv: 'GRV Report',
    report_siv: 'SIV Report',
    report_approval_request: 'Store Request Report',
    report_non_stockable: 'Non- Stockable Report',
    report_return: 'Return Report',
    report_adjust_stock: 'Adjustment Report',
    report_purchase: 'Purchase Report'
  }

  return reportItems.filter((item) => {
    const privilegeName = reportPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const productionItems = [
  { key: 'production_register', label: 'Register' },
  { key: 'production_posting', label: 'Posting' },
  { key: 'production_barbook', label: 'Consumption' },
  { key: 'production_material_inventory', label: 'Material Inventory' },
  { key: 'production_edit_access', label: 'Edit Access' }, 
  { key: 'production_report', label: 'Report' },
]

const posItems = [
  { key: 'pos', label: 'Orders' },
  { key: 'pos_special_order', label: 'Special Orders' },
  { key: 'management_order', label: 'Management Order' },
  { key: 'pos_display', label: 'Display' },
  { key: 'pos_report', label: 'Sales Report' },
  { key: 'pos_special_order_report', label: 'Special O. Report' },
  { key: 'void_pos_report', label: 'Void Report' },
  { key: 'pos_transfer_report', label: 'Transfer Report' },
]

const financeItems = [
  { key: 'finance', label: 'Finance' },
  { key: 'finance_report', label: 'Finance Report' },
]

const hrItems = [
  { key: 'hr_settings', label: 'HR Register' },
  { key: 'hr_advanced_settings', label: 'HR Advanced Register' },
  { key: 'hr_employee', label: 'Employee' },
  { key: 'hr_attendance', label: 'Attendance' },
  { key: 'hr_payroll_approval', label: 'Payroll Approval' },
  { key: 'hr_report', label: 'HR Report' },
]

const hrSettingsItems = [
  // { key: 'hr_company_profile', label: 'Company Profile' },
  { key: 'hr_annual_leaving_type', label: 'Leaving Type' },
  { key: 'hr_performance_criteria', label: 'Performance Criteria' },
]

const hrSettingsPrivilegeMap = {
  hr_company_profile: 'HR Company Profile',
  hr_annual_leaving_type: 'HR Leaving Type',
  hr_performance_criteria: 'HR Performance Criteria',
}

const filteredHrSettingsItems = computed(() => {
  if (hasAdminPrivilege.value) return hrSettingsItems

  return hrSettingsItems.filter((item) => {
    const privilegeName = hrSettingsPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hrAdvancedSettingsItems = [
  { key: 'hr_work_shift_time', label: 'Work/Shift Time' },
  { key: 'hr_overtime_type', label: 'Overtime Type' },
  { key: 'hr_payment_type', label: 'Earn And Deduct' },
  { key: 'hr_payroll_tax_setting', label: 'Pension And TAX' },
]

const hrAdvancedSettingsPrivilegeMap = {
  hr_work_shift_time: 'HR Work/Shift Time',
  hr_overtime_type: 'HR Overtime Type',
  hr_payment_type: 'HR Earn And Deduct',
  hr_payroll_tax_setting: 'HR Pension And TAX',
}

const filteredHrAdvancedSettingsItems = computed(() => {
  if (hasAdminPrivilege.value) return hrAdvancedSettingsItems

  return hrAdvancedSettingsItems.filter((item) => {
    const privilegeName = hrAdvancedSettingsPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hrEmployeeItems = [
  { key: 'hr_find_employee', label: 'Find Employee' },
  { key: 'hr_annual_leaving', label: 'Leaving Request' },
  { key: 'hr_employee_evaluation', label: 'Employee Evaluation' },
  { key: 'hr_employee_letter', label: 'Employee Letter' },
  // { key: 'hr_pension_list', label: 'Pension List' },
]

const hrEmployeePrivilegeMap = {
  hr_find_employee: 'HR Find Employee',
  hr_annual_leaving: 'HR Leaving Request',
  hr_employee_evaluation: 'HR Employee Evaluation',
  hr_employee_letter: 'HR Employee Letter',
}

const filteredHrEmployeeItems = computed(() => {
  if (hasAdminPrivilege.value) return hrEmployeeItems

  return hrEmployeeItems.filter((item) => {
    const privilegeName = hrEmployeePrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hrAttendanceItems = [
  { key: 'hr_monthly_configuration', label: 'Attendance Config' },
  { key: 'hr_attendance_page', label: 'Device Attendance' },
]

const hrAttendancePrivilegeMap = {
  hr_monthly_configuration: ['HR Attendance Configuration', 'HR Earn And Deduct', 'HR Attendance Configuration'],
  hr_attendance_page: ['HR Device Attendance'],
}

const filteredHrAttendanceItems = computed(() => {
  if (hasAdminPrivilege.value) return hrAttendanceItems

  return hrAttendanceItems.filter((item) => {
    const privilegeNames = hrAttendancePrivilegeMap[item.key] || []
    return privilegeNames.some((privilegeName) => hasAnyActionPrivilege(getPrivilegeBlock(privilegeName)))
  })
})

const hrReportItems = [
  { key: 'hr_payroll_report', label: 'Payroll Report' },
  { key: 'hr_leaving_request_report', label: 'Leaving request' },
  { key: 'hr_evaluation_report', label: 'Evaluation Report' },
  { key: 'hr_letter_report', label: 'Letter Report' },
]

const hrReportPrivilegeMap = {
  hr_payroll_report: 'HR Payroll Report',
  hr_leaving_request_report: 'HR Leaving Request Report',
  hr_evaluation_report: 'HR Evaluation Report',
  hr_letter_report: 'HR Letter Report',
}

const filteredHrReportItems = computed(() => {
  // Hide Payroll Report in HR when Finance module is available (lives under Finance instead)
  const items = hrReportItems.filter((item) => {
    if (item.key === 'hr_payroll_report' && hasFinanceModuleAccess.value) return false
    return true
  })

  if (hasAdminPrivilege.value) return items

  return items.filter((item) => {
    const privilegeName = hrReportPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const hrPrivilegeMap = {
  hr_settings: 'HR Register',
  hr_advanced_settings: 'HR Advanced Register',
  hr_employee: 'HR Employee',
  hr_attendance: 'HR Attendance',
  hr_payroll_approval: 'HR Payroll Approval',
  hr_report: 'HR Report',
}

const filteredHrItems = computed(() => {
  // Hide Payroll Approval in HR when Finance module is available (lives under Finance instead)
  const items = hrItems.filter((item) => {
    if (item.key === 'hr_payroll_approval' && hasFinanceModuleAccess.value) return false
    return true
  })

  if (hasAdminPrivilege.value) return items

  return items.filter((item) => {
    const privilegeName = hrPrivilegeMap[item.key]
    if (hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))) return true
    // Backward-compatible: old "HR Payroll" parent still unlocks Payroll Approval
    if (item.key === 'hr_payroll_approval') {
      return hasAnyActionPrivilege(getPrivilegeBlock('HR Payroll'))
    }
    // Parent visible when at least one child privilege is available
    if (item.key === 'hr_settings') return filteredHrSettingsItems.value.length > 0
    if (item.key === 'hr_advanced_settings') return filteredHrAdvancedSettingsItems.value.length > 0
    if (item.key === 'hr_employee') return filteredHrEmployeeItems.value.length > 0
    if (item.key === 'hr_attendance') return filteredHrAttendanceItems.value.length > 0
    if (item.key === 'hr_report') return filteredHrReportItems.value.length > 0
    return false
  })
})

const productionRegisterItems = [
  { key: 'choose_ingredient', label: 'Choose Ingredient' },
   { key: 'production_register_products', label: 'Product' },
  { key: 'recipe', label: 'Recipe' },
  // { key: 'miscellaneous', label: 'Miscellaneous' },
]

const filteredProductionRegisterItems = computed(() => {
  if (hasAdminPrivilege.value) return productionRegisterItems

  const productionRegisterPrivilegeMap = {
    choose_ingredient: 'Choose Ingredient',
    production_register_products: 'Product',
    recipe: 'Recipe'
  }

  return productionRegisterItems.filter((item) => {
    const privilegeName = productionRegisterPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const productionPostingItems = [
  { key: 'daily_inventory', label: 'Daily Inventory' },
  { key: 'daily_products', label: 'Daily Products' },
  { key: 'daily_transaction', label: 'Daily Transaction' },
]

const filteredProductionPostingItems = computed(() => {
  if (hasAdminPrivilege.value) return productionPostingItems

  const productionPostingPrivilegeMap = {
    daily_inventory: 'Daily Inventory',
    daily_products: 'Daily Products',
    daily_transaction: 'Daily Transaction'
  }

  return productionPostingItems.filter((item) => {
    const privilegeName = productionPostingPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const centralKitchenPrivilegeMap = {
  cent_kitchen_departments: 'Cnt. Kitchen Departments',
  central_departments: 'Central Departments',
  central_products: 'Central Products',
 // central_recipe: 'Central Recipe',
  central_inventory: 'Central Inventory',
  product_distribution: 'Central Production',
  items_control: 'Items Control',
  central_report: 'Central Report',
}

const centralKitchenItems = [
  // { key: 'cent_kitchen_departments', label: 'Cnt. Kitchen Dept' },
  { key: 'central_departments', label: 'Central Department' },
  { key: 'central_products', label: 'Central Products' },
 // { key: 'central_recipe', label: 'Central Recipe' },
 { key: 'central_inventory', label: 'Central Inventory' },
{ key: 'product_distribution', label: 'Central Production' },
  { key: 'items_control', label: 'Items Control' },
  { key: 'central_report', label: 'Central Report' },
]

const centralReportItems = [
  { key: 'central_cost', label: 'Central Cost' },
  { key: 'central_product_report', label: 'Product report' },
  { key: 'items_control_report', label: 'Items Control Report' },
]

const filteredCentralReportItems = computed(() => {
  if (hasAdminPrivilege.value) return centralReportItems

  const centralReportPrivilegeMap = {
    central_cost: 'Central Cost',
    central_product_report: 'Product Report',
    items_control_report: 'Items Control Report',
  }

  return centralReportItems.filter((item) => {
    const privilegeName = centralReportPrivilegeMap[item.key]
    if (hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))) return true
    if (item.key === 'items_control_report') {
      return hasAddEditViewPrivilege(getPrivilegeBlock('Items Control Report'))
    }
    // New report entries: allow when user has top-level Cnt. Kitchen access
    return hasAddEditViewPrivilege(getPrivilegeBlock('Cnt. Kitchen'))
  })
})

const filteredCentralKitchenItems = computed(() => {
  if (hasAdminPrivilege.value) {
    if (showCentralReportDropdown.value) {
      return centralKitchenItems.filter((i) => i.key === 'central_report')
    }
    return centralKitchenItems
  }

  if (showCentralReportDropdown.value) {
    return centralKitchenItems.filter((item) => {
      if (item.key !== 'central_report') return false
      return filteredCentralReportItems.value.length > 0
        || hasAddEditViewPrivilege(getPrivilegeBlock('Central Report'))
        || hasAddEditViewPrivilege(getPrivilegeBlock('Cnt. Kitchen'))
    })
  }

  return centralKitchenItems.filter((item) => {
    const privilegeName = centralKitchenPrivilegeMap[item.key]
    if (hasAddEditViewPrivilege(getPrivilegeBlock(privilegeName))) return true
    if (item.key === 'cent_kitchen_departments' || item.key === 'central_departments') {
      // Fallback: allow when user has top-level Cnt. Kitchen access
      return hasAddEditViewPrivilege(getPrivilegeBlock('Cnt. Kitchen'))
    }
    if (item.key === 'items_control') {
      // Backward-compatible: some deployments used "Items Control"
      return hasAddEditViewPrivilege(getPrivilegeBlock('Items Control'))
    }
    if (item.key === 'central_report') {
      return filteredCentralReportItems.value.length > 0
        || hasAddEditViewPrivilege(getPrivilegeBlock('Central Report'))
        || hasAddEditViewPrivilege(getPrivilegeBlock('Cnt. Kitchen'))
    }
    return false
  })
})

const debonesPrivilegeMap = {
  debone_percentage: 'Debone Percentage',
  debone_cost: 'Debone Cost',
  // items_count: 'Debone Inventory',
  // deboni_distribute: 'Debone Controls',
  // deboning_report: 'Debone Report',
}

const debonesItems = [
  { key: 'debone_percentage', label: 'Debone Percentage' },
  { key: 'debone_cost', label: 'Debone Cost' },
  // { key: 'items_count', label: 'Debone Inventory' },
  // { key: 'deboni_distribute', label: 'Debone Controls' },
  // { key: 'deboning_report', label: 'Debone Report' },
]

const filteredDebonesItems = computed(() => {
  if (hasAdminPrivilege.value) return debonesItems

  return debonesItems.filter((item) => {
    const privilegeName = debonesPrivilegeMap[item.key]
    return hasAddEditViewPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const productionReportItems = [
  { key: 'cost_report', label: 'Cost' },
  { key: 'transaction_report', label: 'Transaction' },
  { key: 'material_report', label: 'Material Report' },
  { key: 'product_report', label: 'Product' },
 // { key: 'deboning_report', label: 'Deboning' },
  { key: 'bar_book_report', label: 'Consumption Report' },
  // { key: 'menu_engineering_report', label: 'Menu Engineering' },
  { key: 'gross_profit_report', label: 'Gross Profit' },
]

const filteredProductionReportItems = computed(() => {
  if (hasAdminPrivilege.value) return productionReportItems

  const productionReportPrivilegeMap = {
    cost_report: 'Cost',
    transaction_report: 'Transaction',
    material_report: 'Material Report',
    product_report: 'Product Report',
    deboning_report: 'Deboning',
    bar_book_report: 'Consumption Report',
    menu_engineering_report: 'Menu Engineering',
    gross_profit_report: 'Gross Profit'
  }

  return productionReportItems.filter((item) => {
    const privilegeName = productionReportPrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

//, miclani

const filteredInventoryItems = computed(() => {
  if (hasAdminPrivilege.value) {
    if (showReportDropdown.value) {
      return inventoryItems.filter(i => i.key === 'report_list')
    }
    return inventoryItems
  }

  const inventoryPrivilegeMap = {
    item: 'Item Register',
    grv: 'GRV',
    store_request: 'Store Request',
    approval: 'Approval',
    siv: 'SIV (Internal Issue)',
    non_stockable: 'Non- Stockable',
    return: 'Return',
    adjust_stock: 'Stock Adjustment',
    purchase_request: 'Purchase Request',
    report_list: 'Inv Report'
  }

  if (showReportDropdown.value) {
    return inventoryItems.filter(i => {
      if (i.key !== 'report_list') return false
      const privilegeName = inventoryPrivilegeMap[i.key]
      return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
    })
  }

  return inventoryItems.filter(i => {
    if (i.key === 'approval') {
      // Visible when user can Approve Store Request, Return, or Purchase Request
      return (
        !!getPrivilegeBlock('Store Request')?.Approve ||
        !!getPrivilegeBlock('Return')?.Approve ||
        !!getPrivilegeBlock('Purchase Request')?.Approve
      )
    }
    const privilegeName = inventoryPrivilegeMap[i.key]
    if (i.key === 'store_request' || i.key === 'return' || i.key === 'purchase_request') {
      return hasAddPrivilege(getPrivilegeBlock(privilegeName))
    }
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const filteredProductionItems = computed(() => {
  const productionPrivilegeMap = {
    production_register: 'Register',
    production_posting: 'Posting',
    production_barbook: 'Consumption',
    production_material_inventory: 'Material Inventory',
    production_edit_access: 'Edit Access',
    production_report: 'Report'
  }

  if (hasAdminPrivilege.value) {
    if (showProductionReportDropdown.value) {
      return productionItems.filter((p) => p.key === 'production_report')
    }
    return productionItems
  }

  if (showProductionReportDropdown.value) {
    return productionItems.filter((p) => {
      if (p.key !== 'production_report') return false
      const privilegeName = productionPrivilegeMap[p.key]
      return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
    })
  }

  return productionItems.filter(p => {
    const privilegeName = productionPrivilegeMap[p.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const filteredPosItems = computed(() => {
  if (hasAdminPrivilege.value) return posItems

  return posItems.filter(item => {
    let checkKey = ''
    if (item.key === 'pos') checkKey = 'Orders'
    else if (item.key === 'pos_special_order') checkKey = 'Special Orders'
    else if (item.key === 'pos_special_order_report') checkKey = 'Special O. Report'
    else if (item.key === 'management_order') checkKey = 'Management Order'
    else if (item.key === 'pos_display') checkKey = 'Display'
    else if (item.key === 'pos_report') checkKey = 'Sales Report'
    else if (item.key === 'void_pos_report') checkKey = 'Void Report'
    else if (item.key === 'pos_transfer_report') checkKey = 'Transfer Report'

    const p = getPrivilegeBlock(checkKey)

    if (item.key === 'pos_special_order') {
      return hasAddEditViewPrivilege(p)
    }

    if (item.key === 'pos_special_order_report') {
      return hasViewPrivilege(p)
    }

    if (item.key === 'pos_display' || item.key === 'management_order') {
      return p && (p.Add || p.Edit || p.View || p.Void || p.Approve)
    }

    return p && (p.Add || p.Edit || p.View || p.Void)
  })
})

const filteredFinanceItems = computed(() => {
  if (hasAdminPrivilege.value) return financeItems

  const financePrivilegeMap = {
    finance: 'Finance',
    finance_report: 'Finance Report'
  }

  return financeItems.filter((item) => {
    const privilegeName = financePrivilegeMap[item.key]
    return hasAnyActionPrivilege(getPrivilegeBlock(privilegeName))
  })
})

const items = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'users', label: 'Users' },
  { key: 'inventory', label: 'Stock' },
  { key: 'finance', label: 'Finance' },
  { key: 'production', label: 'Production' },
  { key: 'debones', label: 'Debones' },
  { key: 'central_kitchen', label: 'Cnt. Kitchen' },
  { key: 'pos', label: 'POO' },
  { key: 'hr', label: 'HR' },
  { key: 'crm', label: 'CRM' },
  { key: 'settings', label: 'Settings' },
]

const sidebarItems = computed(() => {
  if (loginSource.value === 'waiter') {
    return [
      { key: 'waiter_orders', label: 'Orders' },
      { key: 'waiter_settings', label: 'Settings' },
      { key: 'logout', label: 'Logout' }
    ]
  }

  let visibleItems = items

  // Stock button only visible if user has Stock privilege or is Admin
  if (!hasStockPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'inventory')
  }

  // Finance button only visible if user has Finance privilege or is Admin
  if (!hasFinancePrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'finance')
  }

  // Production button only visible if user has Production privilege or is Admin
  if (!hasProductionPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'production')
  }

  // POS button only visible if user has POS privilege or is Admin
  if (!hasPosPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'pos')
  }

  // Users button only visible if user has Users privilege or is Admin
  if (!hasUsersPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'users')
  }

  // Debones button only visible if user has Debones privilege or is Admin
  if (!hasDebonesPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'debones')
  }

  // Central Kitchen button only visible if user has Central Kitchen privilege or is Admin
  if (!hasCentralKitchenPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'central_kitchen')
  }

  // HR button only visible if user has HR privilege or is Admin
  if (!hasHrPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'hr')
  }

  // CRM button only visible if user has CRM privilege or is Admin
  if (!hasCrmPrivilege.value) {
    visibleItems = visibleItems.filter(i => i.key !== 'crm')
  }

  return visibleItems
})

const registerInsertAfterKey = computed(() => {
  // Keep Register near Users when Users is visible; otherwise anchor it after Dashboard.
  return sidebarItems.value.some(item => item.key === 'users') ? 'users' : 'dashboard'
})

// Hide header for inventory children (except Item Register) and for report pages
const hideHeader = computed(() =>
  active.value === 'dashboard' ||
  route.name === 'dashboard' ||
  route.path === '/dashboard' ||
  active.value === 'finance' ||
  active.value === 'finance_report' ||
  active.value === 'hr_company_profile' ||
  active.value === 'hr_annual_leaving_type' ||
  active.value === 'hr_performance_criteria' ||
  active.value === 'hr_work_shift_time' ||
  active.value === 'hr_overtime_type' ||
  active.value === 'hr_payment_type' ||
  active.value === 'hr_payroll_tax_setting' ||
  active.value === 'hr_find_employee' ||
  active.value === 'hr_annual_leaving' ||
  active.value === 'hr_employee_evaluation' ||
  active.value === 'hr_employee_letter' ||
  active.value === 'hr_monthly_configuration' ||
  active.value === 'hr_attendance_page' ||
  active.value === 'hr_payroll_approval' ||
  active.value === 'hr_payroll_report' ||
  active.value === 'hr_leaving_request_report' ||
  active.value === 'hr_evaluation_report' ||
  active.value === 'hr_letter_report' ||
  active.value === 'pos' ||
  active.value === 'pos_special_order' ||
  route.name === 'pos_special_order' ||
  active.value === 'pos_special_order_report' ||
  route.name === 'pos_special_order_report' ||
  active.value === 'pos_display' ||
  active.value === 'pos_report' ||
  active.value === 'void_pos_report' ||
  active.value === 'pos_transfer_report' ||
  active.value === 'waiter_settings' ||
  inventoryItems.some(i => i.key === active.value && i.key !== 'item') ||
  reportItems.some(i => i.key === active.value) ||
  productionReportItems.some(i => i.key === active.value)
)

const breadcrumb = computed(() => {
  // Check main items
  const mainFound = items.find(i => i.key === active.value)
  if (mainFound) return { parent: mainFound.label }

  // Check register items
  const regFound = registerItems.find(i => i.key === active.value)
  if (regFound) return { parent: 'Register', child: regFound.label }

  // Check inventory items
  const invFound = inventoryItems.find(i => i.key === active.value)
  if (invFound) {
    const parentLabel = items.find(i => i.key === 'inventory')?.label || 'Inventory'
    return { parent: parentLabel, child: invFound.label }
  }

  // Check report routes
  const repFound = reportItems.find(i => i.key === active.value)
  if (repFound) {
    const parentLabel = items.find(i => i.key === 'inventory')?.label || 'Inventory'
    return { parent: parentLabel, child: repFound.label }
  }

  // Check production routes
  const prodFound = productionItems.find(i => i.key === active.value)
  if (prodFound) {
    const parentLabel = items.find(i => i.key === 'production')?.label || 'Production'
    return { parent: parentLabel, child: prodFound.label }
  }

  // Check production register routes
  const prodRegFound = productionRegisterItems.find(i => i.key === active.value)
  if (prodRegFound) {
    return { parent: 'Register', child: prodRegFound.label }
  }

  // Check production posting routes
  const prodPostFound = productionPostingItems.find(i => i.key === active.value)
  if (prodPostFound) {
    return { parent: 'Posting', child: prodPostFound.label }
  }

  // Check central report routes
  const centralReportFound = centralReportItems.find(i => i.key === active.value)
  if (centralReportFound) {
    return { parent: 'Central Report', child: centralReportFound.label }
  }

  // Check central kitchen routes
  const centralKitchenFound = centralKitchenItems.find(i => i.key === active.value)
  if (centralKitchenFound) {
    const parentLabel = items.find(i => i.key === 'central_kitchen')?.label || 'Cnt. Kitchen'
    return { parent: parentLabel, child: centralKitchenFound.label }
  }

  // Check debones routes
  const debonesFound = debonesItems.find(i => i.key === active.value)
  if (debonesFound) {
    const parentLabel = items.find(i => i.key === 'debones')?.label || 'Debones'
    return { parent: parentLabel, child: debonesFound.label }
  }

  if (active.value === 'notifications') {
    return { parent: 'Notifications' }
  }

  // Check POS routes
  const posFound = posItems.find(i => i.key === active.value)
  if (posFound) {
    return { parent: 'POS', child: posFound.label }
  }

  const financeFound = financeItems.find(i => i.key === active.value)
  if (financeFound) {
    const parentLabel = items.find(i => i.key === 'finance')?.label || 'Finance'
    return { parent: parentLabel, child: financeFound.label }
  }

  const hrFound = hrItems.find(i => i.key === active.value)
  if (hrFound) {
    const parentLabel = items.find(i => i.key === 'hr')?.label || 'HR'
    return { parent: parentLabel, child: hrFound.label }
  }
  const hrSettingsFound = hrSettingsItems.find(i => i.key === active.value)
  if (hrSettingsFound) {
    return { parent: 'HR Register', child: hrSettingsFound.label }
  }
  const hrAdvancedSettingsFound = hrAdvancedSettingsItems.find(i => i.key === active.value)
  if (hrAdvancedSettingsFound) {
    return { parent: 'Advanced Register', child: hrAdvancedSettingsFound.label }
  }
  const hrEmployeeFound = hrEmployeeItems.find(i => i.key === active.value)
  if (hrEmployeeFound) {
    return { parent: 'Employee', child: hrEmployeeFound.label }
  }
  const hrAttendanceFound = hrAttendanceItems.find(i => i.key === active.value)
  if (hrAttendanceFound) {
    return { parent: 'Attendance', child: hrAttendanceFound.label }
  }
  if (active.value === 'hr_payroll_approval') {
    return { parent: items.find(i => i.key === 'hr')?.label || 'HR', child: 'Payroll Approval' }
  }
  const hrReportFound = hrReportItems.find(i => i.key === active.value)
  if (hrReportFound) {
    return { parent: 'HR Report', child: hrReportFound.label }
  }
  if (active.value === 'hr') {
    return { parent: items.find(i => i.key === 'hr')?.label || 'HR' }
  }

  return { parent: 'Dashboard' }
})

const updateDropdownsFromRoute = (routeName) => {
  if (!routeName) return
  
  const isRegister = registerItems.some(i => i.key === routeName)
  const isInventory = inventoryItems.some(i => i.key === routeName)
  const isReport = reportItems.some(i => i.key === routeName)
  const isProduction = productionItems.some(i => i.key === routeName)
  const isProductionRegister = productionRegisterItems.some(i => i.key === routeName)
  const isProductionPosting = productionPostingItems.some(i => i.key === routeName)
  const isCentralKitchen = centralKitchenItems.some(i => i.key === routeName)
  const isDebones = debonesItems.some(i => i.key === routeName)
  const isProductionReport = productionReportItems.some(i => i.key === routeName)
  const isCentralReport = centralReportItems.some(i => i.key === routeName)
  const isPos = posItems.some(i => i.key === routeName)
  const isFinance = financeItems.some(i => i.key === routeName)
  const isHrSettings = hrSettingsItems.some(i => i.key === routeName)
  const isHrAdvancedSettings = hrAdvancedSettingsItems.some(i => i.key === routeName)
  const isHrEmployee = hrEmployeeItems.some(i => i.key === routeName)
  const isHrAttendance = hrAttendanceItems.some(i => i.key === routeName)
  const isHrReport = hrReportItems.some(i => i.key === routeName)
  const isHrPayrollApproval = routeName === 'hr_payroll_approval'
  const isHr = hrItems.some(i => i.key === routeName) || isHrSettings || isHrAdvancedSettings || isHrEmployee || isHrAttendance || isHrReport || isHrPayrollApproval || routeName === 'hr'

  if (isRegister) {
    if (!showRegisterDropdown.value) showRegisterDropdown.value = true
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showCentralReportDropdown.value = false
  } else if (isInventory || isReport) {
    if (!showInventoryDropdown.value) showInventoryDropdown.value = true
    showRegisterDropdown.value = false
    showReportDropdown.value = isReport
    showProductionDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showCentralReportDropdown.value = false
  } else if (isProduction) {
    showProductionDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isDebones) {
    showDebonesDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isCentralReport) {
    showCentralKitchenDropdown.value = true
    showCentralReportDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isCentralKitchen) {
    showCentralKitchenDropdown.value = true
    showCentralReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isProductionRegister) {
    showProductionDropdown.value = true
    showProductionRegisterDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralReportDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isProductionPosting) {
    showProductionDropdown.value = true
    showProductionPostingDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionRegisterDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isProductionReport) {
    showProductionDropdown.value = true
    showProductionReportDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  } else if (isFinance) {
    showFinanceDropdown.value = true
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showPosDropdown.value = false
    showCentralReportDropdown.value = false
  } else if (isPos || routeName === 'void_pos_report') {
    showPosDropdown.value = true
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showCentralReportDropdown.value = false
  } else if (isHr) {
    showHrDropdown.value = true
    showHrSettingsDropdown.value = isHrSettings
    showHrAdvancedSettingsDropdown.value = isHrAdvancedSettings
    showHrEmployeeDropdown.value = isHrEmployee
    showHrAttendanceDropdown.value = isHrAttendance
    showHrReportDropdown.value = isHrReport
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showCentralReportDropdown.value = false
  } else if (items.some(i => i.key === routeName)) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showCentralReportDropdown.value = false
  }
}

onMounted(() => {
  updateDropdownsFromRoute(route.name)
  window.addEventListener('toggle-sidebar', () => {
    if (window.innerWidth >= 768 && window.innerWidth < 1280) {
      tabletSidebarVisible.value = !tabletSidebarVisible.value
      window.dispatchEvent(new CustomEvent('tablet-sidebar-visibility', {
        detail: tabletSidebarVisible.value
      }))
    } else {
      isMobileSidebarOpen.value = !isMobileSidebarOpen.value
    }
  })
})

watch(
  () => route.name,
  name => {
    active.value = name
    // Also check for 'item' route which might be active from sub-menu
    if (name === 'item') {
      active.value = 'item'
    }
    isMobileSidebarOpen.value = false
    tabletSidebarVisible.value = true
    window.dispatchEvent(new CustomEvent('tablet-sidebar-visibility', { detail: true }))
    updateDropdownsFromRoute(name)
  }
)

function handleAddUser() {
  if (!hasUsersAddPrivilege.value) return
  showUserModal.value = true
}

function handleAddItem() {
  if (!hasItemRegisterAddPrivilege.value) return
  // Dispatch event to itemPage.vue
  window.dispatchEvent(new CustomEvent('open-item-modal'))
}

function handleAddDepartment() {
  if (!hasDepartmentAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-department-modal'))
}

function handleAddCentralDepartment() {
  if (!hasCentralDepartmentAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-central-department-modal'))
}

function handleAddCategory() {
  if (!hasCategoryAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-category-modal'))
}

function handleAddRole() {
  if (!hasRolesAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-role-modal'))
}

function handleAddSupplier() {
  if (!hasSupplierAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-supplier-modal'))
}

function handleAddMenu() {
  if (!hasMenuListAddPrivilege.value) return
  window.dispatchEvent(new CustomEvent('open-menu-list-modal'))
}

function handleUserRegistered() {
  // Dispatch custom event to notify usersPage.vue
  window.dispatchEvent(new CustomEvent('users-updated'))
}

function handleChangePassword() {
  router.push({ name: 'waiter_settings' })
}

function onServerDateTimeSelected() {
  if (!hasChangeServerDatePrivilege.value) return
  const date = String(serverDateTimeForm.value.date || '').trim()
  if (!date) {
    showToast('Please select a date', 'error')
    return
  }

  const time = String(serverDateTimeForm.value.time || '00:00:00').trim()
  const dateTime = `${date} ${time}`

  serverDateModal.value = {
    open: true,
    dateTime,
    saving: false,
    error: '',
  }
}

function closeServerDateModal() {
  if (serverDateModal.value.saving) return
  serverDateModal.value.open = false
  serverDateModal.value.error = ''
  serverDateTimeForm.value = {
    date: '',
    time: '00:00:00',
  }
}

async function confirmChangeServerDate() {
  if (!hasChangeServerDatePrivilege.value) return
  const dateTime = String(serverDateModal.value.dateTime || '').trim()
  if (!dateTime) return

  serverDateModal.value.saving = true
  serverDateModal.value.error = ''

  try {
    const res = await apiFetch('/settings/server-date', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: dateTime }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      serverDateModal.value.error = data?.message || 'Failed to update server date.'
      showToast(serverDateModal.value.error, 'error')
      return
    }
    serverDateModal.value.open = false
    serverDateTimeForm.value = {
      date: '',
      time: '00:00:00',
    }
    showToast('Server date updated successfully.', 'success')
  } catch (err) {
    console.error('Error updating server date:', err)
    serverDateModal.value.error = 'Failed to update server date.'
    showToast('Failed to update server date.', 'error')
  } finally {
    serverDateModal.value.saving = false
  }
}

function handleLogout() {
  const source = store.getters.loginSource
  store.dispatch('logout')
  if (source === 'waiter') {
    router.push({ name: 'waiter_login' })
  } else {
    router.push({ name: 'login' })
  }
}

function handleNavClick(key) {
//   if (key === 'hr') {
//     if (!hasHrModuleAccess.value) return
//     return
//   }
//   if (key === 'crm') {
//     if (!hasCrmModuleAccess.value) return
//     return
//   }
//   if (key === 'central_departments' || key === 'central_products' || key === 'central_inventory' || key === 'product_distribution' || key === 'items_control') {
//   return;
//  }
  isMobileSidebarOpen.value = false
  if (key === 'logout') {
    isMobileSidebarOpen.value = false
    handleLogout()
    return
  }
  if (key === 'waiter_orders') {
    active.value = 'pos'
    router.push({ name: 'pos' })
    
    // Ensure all dropdowns are closed upon going to order view directly
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    return
  }

  // HR Settings with dedicated routes (other HR settings stay sidebar-only for now)
  if (key === 'hr_company_profile' || key === 'hr_annual_leaving_type' || key === 'hr_performance_criteria') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrSettingsDropdown.value = true
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Advanced Settings with dedicated routes
  if (key === 'hr_work_shift_time' || key === 'hr_overtime_type' || key === 'hr_payment_type' || key === 'hr_payroll_tax_setting') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrAdvancedSettingsDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR submenu: sidebar selection only for now (no dedicated routes yet)
  if (hrSettingsItems.some(i => i.key === key)) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrSettingsDropdown.value = true
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    return
  }

  if (hrAdvancedSettingsItems.some(i => i.key === key)) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrAdvancedSettingsDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    return
  }

  // HR Employee routes with dedicated pages
  if (
    key === 'hr_find_employee' ||
    key === 'hr_annual_leaving' ||
    key === 'hr_employee_evaluation' ||
    key === 'hr_employee_letter'
  ) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrEmployeeDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  if (hrEmployeeItems.some(i => i.key === key)) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrEmployeeDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    return
  }

  // HR Attendance routes with dedicated pages
  if (key === 'hr_monthly_configuration' || key === 'hr_attendance_page') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrAttendanceDropdown.value = true
    showHrReportDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  if (hrAttendanceItems.some(i => i.key === key)) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrAttendanceDropdown.value = true
    showHrReportDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Payroll Approval - direct route (no submenu)
  if (key === 'hr_payroll_approval') {
    if (!hasHrModuleAccess.value) return
    if (hasFinanceModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Payroll Report - under HR Report dropdown
  if (key === 'hr_payroll_report') {
    if (!hasHrModuleAccess.value) return
    if (hasFinanceModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = true
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Leaving Request Report - under HR Report dropdown
  if (key === 'hr_leaving_request_report') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = true
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Evaluation Report - under HR Report dropdown
  if (key === 'hr_evaluation_report') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = true
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // HR Letter Report - under HR Report dropdown
  if (key === 'hr_letter_report') {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = true
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    router.push({ name: key })
    return
  }

  // Other HR Report items (sidebar selection; dedicated pages later)
  if (hrReportItems.some(i => i.key === key)) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrReportDropdown.value = true
    showHrAttendanceDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    return
  }

  if (hrItems.some(i => i.key === key && i.key !== 'hr_settings' && i.key !== 'hr_advanced_settings' && i.key !== 'hr_employee' && i.key !== 'hr_attendance' && i.key !== 'hr_report' && i.key !== 'hr_payroll_approval')) {
    if (!hasHrModuleAccess.value) return
    active.value = key
    showHrDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    return
  }

 active.value = key
  router.push({ name: key })
  
  // Only close dropdowns if navigating to a main top-level item 
  // (unless it's a sub-item we want to keep open)
  const isMainItem = items.some(i => i.key === key && i.key !== 'inventory' && i.key !== 'hr')
  
  if (isMainItem) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleRegister() {
  showRegisterDropdown.value = !showRegisterDropdown.value
  if (showRegisterDropdown.value) {
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleInventory() {
  if (!hasStockModuleAccess.value) return

  // If report submenu is open and user clicks Inventory, close report and open inventory
  if (showReportDropdown.value) {
    showReportDropdown.value = false
    showInventoryDropdown.value = true
    showRegisterDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    return
  }

  showInventoryDropdown.value = !showInventoryDropdown.value
  if (showInventoryDropdown.value) {
    showRegisterDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleProduction() {
  if (!hasProductionModuleAccess.value) return

  // close others when toggling production
  showInventoryDropdown.value = false
  showReportDropdown.value = false
  showRegisterDropdown.value = false
  showProductionRegisterDropdown.value = false
  showProductionPostingDropdown.value = false
  showCentralKitchenDropdown.value = false
  showCentralReportDropdown.value = false
  showDebonesDropdown.value = false
  showProductionReportDropdown.value = false
  showPosDropdown.value = false
  showFinanceDropdown.value = false
  showHrDropdown.value = false
  showHrSettingsDropdown.value = false
  showHrAdvancedSettingsDropdown.value = false
  showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  showProductionDropdown.value = !showProductionDropdown.value
}

function toggleProductionRegister() {
  showProductionRegisterDropdown.value = !showProductionRegisterDropdown.value
  if (showProductionRegisterDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = true
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleProductionPosting() {
  showProductionPostingDropdown.value = !showProductionPostingDropdown.value
  if (showProductionPostingDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = true
    showProductionRegisterDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleCentralKitchen() {
  if (!hasCentralKitchenModuleAccess.value) return

  showInventoryDropdown.value = false
  showReportDropdown.value = false
  showRegisterDropdown.value = false
  showProductionDropdown.value = false
  showProductionRegisterDropdown.value = false
  showProductionPostingDropdown.value = false
  showDebonesDropdown.value = false
  showProductionReportDropdown.value = false
  showCentralReportDropdown.value = false
  showPosDropdown.value = false
  showFinanceDropdown.value = false
  showHrDropdown.value = false
  showHrSettingsDropdown.value = false
  showHrAdvancedSettingsDropdown.value = false
  showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  showCentralKitchenDropdown.value = !showCentralKitchenDropdown.value
}

function toggleDebones() {
  if (!hasDebonesModuleAccess.value) return

  showInventoryDropdown.value = false
  showReportDropdown.value = false
  showRegisterDropdown.value = false
  showProductionDropdown.value = false
  showProductionRegisterDropdown.value = false
  showProductionPostingDropdown.value = false
  showCentralKitchenDropdown.value = false
  showCentralReportDropdown.value = false
  showProductionReportDropdown.value = false
  showPosDropdown.value = false
  showFinanceDropdown.value = false
  showHrDropdown.value = false
  showHrSettingsDropdown.value = false
  showHrAdvancedSettingsDropdown.value = false
  showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  showDebonesDropdown.value = !showDebonesDropdown.value
}

function toggleProductionReport() {
  showProductionReportDropdown.value = !showProductionReportDropdown.value
  if (showProductionReportDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = true
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleCentralReport() {
  showCentralReportDropdown.value = !showCentralReportDropdown.value
  if (showCentralReportDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showProductionReportDropdown.value = false
    showCentralKitchenDropdown.value = true
    showDebonesDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleReport() {
  showReportDropdown.value = !showReportDropdown.value
  if (showReportDropdown.value) {
    // keep inventory open but close register dropdown if open
    showRegisterDropdown.value = false
    showInventoryDropdown.value = true
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function togglePos() {
  if (!hasPosModuleAccess.value) return

  showPosDropdown.value = !showPosDropdown.value
  if (showPosDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showFinanceDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleFinance() {
  if (!hasFinanceModuleAccess.value) return

  showFinanceDropdown.value = !showFinanceDropdown.value
  if (showFinanceDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showHrDropdown.value = false
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleHr() {
  if (!hasHrModuleAccess.value) return

  showHrDropdown.value = !showHrDropdown.value
  if (showHrDropdown.value) {
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  } else {
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
  }
}

function toggleHrSettings() {
  if (!hasHrModuleAccess.value) return

  showHrSettingsDropdown.value = !showHrSettingsDropdown.value
  if (showHrSettingsDropdown.value) {
    showHrDropdown.value = true
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  }
}

function toggleHrAdvancedSettings() {
  if (!hasHrModuleAccess.value) return

  showHrAdvancedSettingsDropdown.value = !showHrAdvancedSettingsDropdown.value
  if (showHrAdvancedSettingsDropdown.value) {
    showHrDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  }
}

function toggleHrEmployee() {
  if (!hasHrModuleAccess.value) return

  showHrEmployeeDropdown.value = !showHrEmployeeDropdown.value
  if (showHrEmployeeDropdown.value) {
    showHrDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrAttendanceDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  }
}

function toggleHrReport() {
  if (!hasHrModuleAccess.value) return

  showHrReportDropdown.value = !showHrReportDropdown.value
  if (showHrReportDropdown.value) {
    showHrDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrAttendanceDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  }
}

function toggleHrAttendance() {
  if (!hasHrModuleAccess.value) return

  showHrAttendanceDropdown.value = !showHrAttendanceDropdown.value
  if (showHrAttendanceDropdown.value) {
    showHrDropdown.value = true
    showHrSettingsDropdown.value = false
    showHrAdvancedSettingsDropdown.value = false
    showHrEmployeeDropdown.value = false
    showHrReportDropdown.value = false
    showRegisterDropdown.value = false
    showInventoryDropdown.value = false
    showReportDropdown.value = false
    showProductionDropdown.value = false
    showProductionRegisterDropdown.value = false
    showProductionPostingDropdown.value = false
    showCentralKitchenDropdown.value = false
    showCentralReportDropdown.value = false
    showDebonesDropdown.value = false
    showProductionReportDropdown.value = false
    showPosDropdown.value = false
    showFinanceDropdown.value = false
  }
}
</script>

<style scoped>
.sidebar::-webkit-scrollbar {
  width: 8px;
}
.sidebar::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.sidebar {
  scrollbar-width: auto;
}

.tablet-sidebar {
  width: 240px;
  transform: translateX(-100%);
}

.tablet-main {
  margin-left: 0;
}

@media (min-width: 768px) and (max-width: 1279px) {
  .tablet-sidebar {
    width: 150px;
    transform: translateX(0);
    transition: none;
  }

  .tablet-sidebar.tablet-sidebar-hidden {
    transform: translateX(-100%);
  }

  .tablet-main {
    margin-left: 150px;
  }

  .tablet-main.tablet-main-expanded {
    margin-left: 0;
  }
}

@media (min-width: 1280px) {
  .tablet-sidebar {
    width: 150px;
    transform: translateX(0);
  }

  .tablet-main {
    margin-left: 150px;
  }
}

@media (max-width: 767px) {
  .tablet-sidebar {
    transition: transform 300ms ease-in-out;
  }

  .tablet-sidebar.mobile-sidebar-open {
    transform: translateX(0);
  }
}

.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-5px);
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
